import React from 'react';
import { Immutable } from 'nuclear-js';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import Chart from './chart/chart';
import PartnerGetters from 'data/partner/getters';
import Reactor from 'data/reactor';
import GlobalChartUtils from 'utils/chart';
import ChartData from 'utils/chart-data';
import { findAcademicYearForTermNumber } from 'utils/terms';
import GrowthGoalsUtils from 'components/student-profile/modules/growth-goals/utils';
import { deepCompare } from 'utils/react';

let Perf = null;

export default CreateReactClass({
    displayName: 'GrowthOverTimeContainer',

    propTypes: {
        courseName: PropTypes.string,
        selectedTermHasCourseSpecificTestEvent: PropTypes.bool.isRequired,
        currentTerm: PropTypes.instanceOf(Immutable.Map),
        districtAveragesLoaded: PropTypes.bool,
        futureTermsSetup: PropTypes.bool,
        isMostRecentTerm: PropTypes.bool.isRequired,
        growthGoals: PropTypes.instanceOf(Immutable.List),
        growthProjectionsLoaded: PropTypes.bool,
        linkingStudiesData: PropTypes.object,
        linkingStudiesLoaded: PropTypes.bool,
        mode: PropTypes.oneOf(['normal', 'expanded']),
        ritQuintiles: PropTypes.instanceOf(Immutable.Map),
        ritQuintilesLoaded: PropTypes.bool,
        selectedSubject: PropTypes.instanceOf(Immutable.Map),
        selectedTermNumber: PropTypes.number.isRequired,
        studentBid: PropTypes.string.isRequired,
        studentTermData: PropTypes.instanceOf(Immutable.List),
        subject: PropTypes.instanceOf(Immutable.Map).isRequired,
        testEvents: PropTypes.instanceOf(Immutable.List).isRequired,
        terms: PropTypes.instanceOf(Immutable.List),
    },

    componentDidUpdate(prevProps, prevState) {
        const wasLoaded =
            prevProps.ritQuintilesLoaded &&
            prevProps.districtAveragesLoaded &&
            prevProps.growthProjectionsLoaded &&
            prevProps.linkingStudiesLoaded;

        // only log out for 1 subject since it will be the same data over and over
        if (Perf && this.props.subject.get('bid') === 1 && !wasLoaded && this.isLoaded()) {
            console.log(`GOT data just finished loading for ${this.props.studentBid}`);
            Perf.stop();
            Perf.printWasted();
        }
    },

    shouldComponentUpdate: function(nextProps, nextState) {
        const isLoaded =
            nextProps.ritQuintilesLoaded &&
            nextProps.districtAveragesLoaded &&
            nextProps.growthProjectionsLoaded &&
            nextProps.linkingStudiesLoaded;
        return isLoaded && !deepCompare(nextProps, this.props, false, 'GOT-container');
    },

    getChartBottom(series) {
        return GlobalChartUtils.findMinForSeries(series) - 10;
    },

    getChartTop(series) {
        return GlobalChartUtils.findMaxForSeries(series) + 10;
    },

    isLoaded() {
        const ritQuintilesLoaded = this.props.ritQuintilesLoaded;
        const districtAveragesLoaded = this.props.districtAveragesLoaded;
        const growthProjectionsLoaded = this.props.growthProjectionsLoaded;
        const linkingStudiesLoaded = this.props.linkingStudiesLoaded;
        const allLoaded =
            ritQuintilesLoaded && districtAveragesLoaded && growthProjectionsLoaded && linkingStudiesLoaded;
        // the following left commented out for debugging
        // const all = {
        //    ritQuintilesLoaded,
        //    districtAveragesLoaded,
        //    growthProjectionsLoaded,
        //    linkingStudiesLoaded
        // };
        //
        // //if( !allLoaded ) {
        //    console.log(`studentBid: ${this.props.studentBid}, subject: ${this.props.subject.get('bid')}, all: ${JSON.stringify(all, null, '\t')}`);
        // //}

        return allLoaded;
    },

    render() {
        const chartDataSeries = ChartData.getSeriesData(this.props);
        const ritQuintilesForChart = chartDataSeries.ritQuintilesForChart;
        const RITSeries = chartDataSeries.RITSeries;
        const growthGoalSeries = chartDataSeries.growthGoalSeries;
        const projectionsSeries = chartDataSeries.projectionsSeries;
        const typicalScoreSeries = chartDataSeries.typicalScoreSeries;
        const districtAveragesSeries = chartDataSeries.districtAveragesSeries;

        const container = 'growth-over-time-for-subject-' + this.props.subject.get('bid');
        const chartCenteredTerm =
            this.props.isMostRecentTerm && this.props.currentTerm
                ? this.props.currentTerm.get('termNumber')
                : this.props.selectedTermNumber;
        const termList = GlobalChartUtils.findTermsForGOTChart(chartCenteredTerm);

        let stateLinkingStudySeries,
            collegeLinkingStudySeries,
            xAxisTermNames,
            discreteQuintileSeries,
            popoverSeries,
            chartBottom,
            chartTop;
        if (termList !== null && termList !== undefined) {
            /* See TP 215501 - check for null - causes 401 on logout during clearState() */
            GlobalChartUtils.addGradeAndWeeksToGOTTerms(this.props.terms, this.props.studentTermData, termList);
            xAxisTermNames = GlobalChartUtils.populateCategoryNames(termList);

            discreteQuintileSeries = GlobalChartUtils.extractDiscreteQuintileData(ritQuintilesForChart);

            const testToProjectFrom = GlobalChartUtils.findTestToProjectFrom(
                this.props.testEvents,
                this.props.currentTerm
            );
            const partnerStudies = Reactor.evaluate(PartnerGetters.linkingStudies);
            const stateLinkingStudy = GlobalChartUtils.findLinkingStudyByType(
                this.props.linkingStudiesData,
                'STATE',
                termList.last().get('termNumber'),
                partnerStudies
            );
            let collegeLinkingStudies = null;

            GlobalChartUtils.collegeLinkingStudyTypes.forEach((studyType) => {
                const study = GlobalChartUtils.findLinkingStudyByType(
                    this.props.linkingStudiesData,
                    'COLLEGE',
                    termList.last().get('termNumber'),
                    partnerStudies,
                    studyType
                );
                if (study) {
                    if (!collegeLinkingStudies) {
                        collegeLinkingStudies = [];
                    }
                    collegeLinkingStudies.push(study);
                }
            });

            const firstOpenTerm = GrowthGoalsUtils.getFirstOpenTerm(
                this.props.terms,
                this.props.subject.get('bid'),
                this.props.testEvents
            );

            const studentHasGradeForCurrentAY =
                this.props.currentTerm && this.props.studentTermData
                    ? this.props.studentTermData.find((t) => {
                          return (
                              findAcademicYearForTermNumber(t.get('termNumber')) ===
                              findAcademicYearForTermNumber(this.props.currentTerm.get('termNumber'))
                          );
                      })
                    : false;

            const goalSettingTerms = GrowthGoalsUtils.findGoalSettingTerms(
                this.props.terms,
                firstOpenTerm,
                this.props.currentTerm,
                GrowthGoalsUtils.findMaxTestEventForSubject(this.props.testEvents, this.props.subject),
                this.props.studentTermData
            );

            const dataArrays = {
                sortedTerms: termList,
                testEvents: this.props.testEvents,
                typicalGrowth: typicalScoreSeries,
                districtAverages: districtAveragesSeries,
                goals: growthGoalSeries,
                projectedGrowth: projectionsSeries,
                collegeLinkingStudies: collegeLinkingStudies,
                stateLinkingStudy: stateLinkingStudy,
                testToProjectFrom: testToProjectFrom,
                goalSettingTerms: goalSettingTerms,
                firstOpenTerm: firstOpenTerm,
                studentHasGradeForCurrentAY: studentHasGradeForCurrentAY,
            };
            popoverSeries = GlobalChartUtils.populateTermDataPopoverSeries(dataArrays, this.props.currentTerm);

            let concatSeries = (RITSeries ? RITSeries : [])
                .concat(typicalScoreSeries ? typicalScoreSeries : [])
                .concat(districtAveragesSeries ? districtAveragesSeries : [])
                .concat(growthGoalSeries ? growthGoalSeries : [])
                .concat(projectionsSeries ? projectionsSeries : []);

            chartBottom = this.getChartBottom([concatSeries]);
            chartTop = this.getChartTop([concatSeries]);

            stateLinkingStudySeries = GlobalChartUtils.populateLinkingStudySeries(
                stateLinkingStudy,
                termList,
                Math.ceil(chartTop / 10) * 10,
                true
            );
            collegeLinkingStudySeries = null;
            if (collegeLinkingStudies) {
                collegeLinkingStudies.forEach((study) => {
                    if (!collegeLinkingStudySeries) {
                        collegeLinkingStudySeries = [];
                    }
                    collegeLinkingStudySeries.push(
                        GlobalChartUtils.populateLinkingStudySeries(
                            study,
                            termList,
                            Math.ceil(chartTop / 10) * 10,
                            true
                        )
                    );
                });
            }
        } else {
            return null;
        }

        return this.props.mode === 'normal' ? (
            <Chart
                courseName={this.props.courseName}
                selectedTermHasCourseSpecificTestEvent={this.props.selectedTermHasCourseSpecificTestEvent}
                chartBottom={chartBottom}
                chartContainer={container}
                chartTop={chartTop}
                collegeLinkingStudySeries={collegeLinkingStudySeries}
                currentStudentBid={this.props.studentBid}
                discreteQuintileSeries={discreteQuintileSeries}
                districtAveragesSeries={districtAveragesSeries}
                futureTermMissing={!this.props.futureTermsSetup}
                growthGoalSeries={growthGoalSeries}
                key="growth-over-time-default-content"
                loaded={this.isLoaded()}
                popoverSeries={popoverSeries}
                projectionsSeries={projectionsSeries}
                quintileSeries={ritQuintilesForChart}
                RITScoreSeries={RITSeries}
                selectedSubject={this.props.selectedSubject}
                stateLinkingStudySeries={stateLinkingStudySeries}
                studentBid={this.props.studentBid}
                subject={this.props.subject}
                typicalScoreSeries={typicalScoreSeries}
                xAxisTerms={xAxisTermNames}
            />
        ) : null;
    },
});
